import { useEffect, useRef, useState } from 'react';

import { Plus } from '../icons.js';

import { findByTitle } from './search.js';

import type { Task } from '@nedelnik/shared';

/**
 * Подбор задачи поиском по названию (3.9.2).
 *
 * Один компонент на связи и на выбор проекта: в обоих случаях вопрос один и
 * тот же — «какая из существующих задач», — и два разных способа его задать
 * человеку пришлось бы изучать дважды.
 *
 * Список кандидатов приходит снаружи уже отфильтрованным: у проекта из него
 * вычтены вложенные задачи и сами проекты (уровней ровно два), у связи — уже
 * связанные. Подборщик не должен показывать вариант, который действие всё
 * равно отвергнет.
 *
 * `onCreate` необязателен: он есть у проекта, потому что нужного зонтика может
 * ещё не существовать, и нет у связи — связывать с только что придуманной
 * пустой задачей незачем.
 *
 * У подборщика два вида. Обычный раскрывается по кнопке и показывает список
 * сразу. Встроенный (`inline`) стоит в карточке всегда — по одному у каждой
 * стороны связи, — и список у него появляется, только когда человек сам встал
 * в поле или начал набирать: два открытых списка подряд заняли бы всю
 * карточку, а вопрос у поля один и написан прямо в нём.
 *
 * **Список существующих задач виден сразу, до всякого ввода**, и это главное
 * свойство подборщика: искать по названию имеет смысл, когда задач много, а
 * выбрать из десятка нужно всегда. Первая версия открывалась с `autoFocus`, и
 * на телефоне это оказалось равносильно отсутствию списка: клавиатура
 * выезжала поверх него, а над ней вставала подсказка браузера с ранее
 * набранным текстом — её и принимали за весь выбор.
 */
export function TaskPicker({
  label,
  placeholder,
  candidates,
  onPick,
  onCreate,
  createLabel,
  notice,
  inline = false,
  initiallyOpen = false,
  addLabel = 'Добавить',
  onCancel,
}: {
  /** Подпись поля. Обязана отличаться от подписи кнопки, открывшей подборщик. */
  label: string;
  placeholder: string;
  candidates: readonly Task[];
  onPick: (task: Task) => void;
  /** Завести новую задачу с набранным названием и выбрать её. */
  onCreate?: (title: string) => void;
  createLabel?: (title: string) => string;
  /** Почему последняя попытка не удалась. Молчать об отказе нельзя. */
  notice?: string | undefined;
  /** Встроенный вид: поле стоит в карточке всегда, список — по фокусу. */
  inline?: boolean;
  /**
   * Встроенный подборщик раскрыт с самого начала — без фокуса в поле, а значит
   * и без клавиатуры поверх списка. Так открывается раздел связи, которого в
   * карточке не было: человек уже нажал «добавить», и показать ему выбор сразу
   * честнее, чем заставлять нажимать второй раз в поле.
   */
  initiallyOpen?: boolean;
  /**
   * Подпись кнопки рядом с полем. Обязана отличаться от подписи самого поля:
   * иначе на экране два элемента управления с одним именем, и в озвучке они
   * неразличимы.
   */
  addLabel?: string;
  /**
   * Человек ушёл, ничего не выбрав: Esc, «Отмена» или фокус покинул поле.
   * Родитель по этому вызову убирает поле совсем — оно стоит в карточке
   * только пока его ждут (см. `Relations`).
   */
  onCancel?: (() => void) | undefined;
}): React.ReactElement {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(initiallyOpen);
  const box = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLDivElement>(null);
  const found = findByTitle(candidates, query, 20);

  const typed = query.trim();
  const exact = candidates.find(
    (task) => (task.title ?? '').toLocaleLowerCase() === typed.toLocaleLowerCase(),
  );
  // Кнопка появляется только когда есть что назвать и когда точно такого
  // названия ещё нет: предлагать завести второй «переезд» рядом с первым
  // значит плодить двойников молча.
  const canCreate = onCreate !== undefined && typed.length > 0 && exact === undefined;

  /**
   * Что делает Enter. Порядок такой, чтобы ответ был однозначным:
   * набранное точно совпало с существующей задачей — берём её; можно завести
   * новую — заводим; осталось ровно одно совпадение — берём его. Во всех
   * остальных случаях Enter молчит: выбирать за человека из нескольких
   * вариантов подборщик не вправе.
   */
  function submit(): void {
    if (exact !== undefined) {
      pick(exact);
      return;
    }
    if (canCreate) {
      onCreate?.(typed);
      setQuery('');
      return;
    }
    const single = found.length === 1 ? found[0] : undefined;
    if (single !== undefined) pick(single);
  }

  // Встроенное поле раскрывает список по фокусу и гасит его, когда фокус ушёл.
  //
  // Наивное `onBlur` здесь не работает: нажатие по варианту само снимает фокус
  // с поля, и список исчезал бы раньше, чем срабатывал выбор. Поэтому фокус из
  // поля не выпускают вовсе — `mousedown` внутри списка отменяется, и нажатие
  // доходит до кнопки, не трогая фокус. Проверять `relatedTarget` в `blur`
  // было бы ненадёжно: Safari по нажатию кнопку не фокусирует, там оказался бы
  // `null`, неотличимый от ухода со страницы.
  const open = inline ? active : true;

  // Подборщик раскрывается посреди длинной панели, и без этого он появляется
  // ниже границы экрана: человек видит, что кнопка сработала, но не видит чем.
  //
  // Вызов необязательный (`?.()`): в jsdom метода нет вовсе, и обычное
  // обращение уронило бы всё дерево React — то есть прокрутка, вещь сама по
  // себе косметическая, гасила бы страницу там, где её не реализовали.
  useEffect(() => {
    if (!open) return;
    // К верху экрана поднимается строка с полем, а не весь подборщик: у
    // раскрытого подборщика верх — это поле, а низ — список, и выравнивание по
    // всей коробке уводило её верхнюю часть за границу видимого. Плавность
    // убрана намеренно: браузер в этот же момент везёт к полю клавиатуру, и
    // две прокрутки наперегонки дают непредсказуемое место остановки.
    row.current?.scrollIntoView?.({ block: 'start' });
  }, [open]);

  function pick(chosen: Task): void {
    onPick(chosen);
    collapse();
  }

  /**
   * Свернуть встроенное поле.
   *
   * Фокус снимается явно. Он остаётся в поле намеренно (см. `open` ниже), и
   * без снятия следующее нажатие по полю не вызвало бы `focus` — поле уже
   * сфокусировано, — а значит, список больше не открылся бы никогда.
   */
  function collapse(): void {
    setQuery('');
    setActive(false);
    box.current?.querySelector<HTMLInputElement>('.picker__input')?.blur();
  }

  /**
   * Уйти, ничего не выбрав. `collapse` снимает фокус, и `blur` сам сообщит
   * родителю; но раскрытый с самого начала подборщик (`initiallyOpen`) фокуса
   * может и не иметь — тогда `blur` не случится, и сказать надо явно.
   */
  function dismiss(): void {
    collapse();
    onCancel?.();
  }

  return (
    <div
      /*
        Оформление у подборщика одно на оба вида (3.9.2, 3.8.3): рамка с
        подложкой описана в `.picker`, а `.picker--bare` её снимает. Свернутое
        встроенное поле — это просто поле, коробка вокруг него была бы обещанием
        списка, которого пока нет.
      */
      className={inline ? `picker picker--inline${open ? '' : ' picker--bare'}` : 'picker'}
      ref={box}
      // Фокус ушёл — человек ушёл: список гаснет, а родитель узнаёт об этом и
      // убирает поле. Нажатие по варианту фокуса не снимает (см. ниже).
      onBlur={
        inline
          ? () => {
              setActive(false);
              onCancel?.();
            }
          : undefined
      }
      onMouseDown={
        inline
          ? (event) => {
              // Фокус остаётся в поле: только так `blur` означает «человек
              // ушёл», а не «человек выбирает из списка».
              if (event.target !== box.current?.querySelector('.picker__input')) {
                event.preventDefault();
              }
            }
          : undefined
      }
    >
      <div className="picker__row" ref={row}>
        <input
          className="picker__input"
          type="text"
          aria-label={label}
          placeholder={placeholder}
          /*
            Браузер иначе предлагает свои прошлые значения поверх нашего
            списка — и подсказка «Задача 1» из чужого поля выглядит как весь
            доступный выбор. Поле ищет по собственным данным человека, чужой
            истории ему предлагать нечего.
          */
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          value={query}
          onFocus={() => setActive(true)}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              // Подборщик — не диалог: Esc закрывает его, а не всю панель.
              event.stopPropagation();
              dismiss();
              return;
            }
            if (event.key === 'Enter') {
              // Поле не в форме, и без этого Enter не делает ровно ничего:
              // человек набирает название, жмёт Enter и не получает ни
              // результата, ни объяснения. Везде в продукте добавление
              // работает и с клавиатуры, и видимой кнопкой (3.3.4), и здесь
              // обязано работать так же.
              event.preventDefault();
              submit();
            }
          }}
        />
        {inline && !open ? (
          // Кнопка рядом с полем обязательна везде, где что-то добавляется
          // (3.3.4): на мобильной клавиатуре Enter до поля доходит не всегда.
          <button type="button" className="miniadd" aria-label={addLabel} onClick={submit}>
            <Plus />
          </button>
        ) : (
          // Раскрытый подборщик выглядит одинаково везде, поэтому и здесь
          // «Отмена», а не плюс: две формы одного блока различались бы только
          // тем, каким способом их закрывают.
          //
          // Ссылка, а не кнопка: «Отмена» никуда не ведёт и ничего не делает
          // с задачей, ей не положен ни цвет действия, ни белая плашка, которая
          // рядом с белым полем сливалась бы с ним.
          <button type="button" className="link" onClick={dismiss}>
            Отмена
          </button>
        )}
      </div>

      {open && canCreate && (
        <button
          type="button"
          className="picker__create"
          onClick={() => {
            onCreate?.(typed);
            setQuery('');
          }}
        >
          {createLabel?.(typed) ?? `Завести «${typed}»`}
        </button>
      )}

      {open && notice !== undefined && notice.length > 0 && (
        <p className="picker__notice" role="alert">
          {notice}
        </p>
      )}

      {open &&
        (found.length === 0 ? (
          <p className="picker__empty">
            {candidates.length === 0
              ? 'Выбрать не из чего: подходящих задач нет'
              : 'Ничего не нашлось — попробуйте другое слово'}
          </p>
        ) : (
          <>
            {/*
            Заголовок стоит не для красоты: без него ряд названий под полем
            ввода читается как подсказка браузера, а не как список, из которого
            выбирают. Число говорит, весь ли список показан.
          */}
            <span className="picker__caption">
              Существующие задачи{found.length < candidates.length && ` · показано ${found.length}`}
            </span>
            <ul className="picker__list" aria-label="Существующие задачи">
              {found.map((task) => (
                <li key={task.id}>
                  <button type="button" className="picker__item" onClick={() => pick(task)}>
                    <span className="picker__title">{task.title}</span>
                    {task.status === 'done' && <span className="tag">закрыта</span>}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ))}
    </div>
  );
}
